/**
 * SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/tenant-admin/TenantSecurityDashboard.tsx
 * PURPOSE: Tenant Admin Security Dashboard — overview, events, active sessions
 * SDLC FRAMEWORK VERSION: 6.3.1 (7-Pillar + 3-Section + 8-Mental-Model + Agentic)
 * DATE: 2026-03-21
 * COMPLIANCE: SDLC 6.3.1 + DD-0002 RBAC
 * PILLAR: P1 (Stage 04 - BUILD) + P6 (UI Components)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Tenant Admin Module 8 — Security visibility for tenant admins
 * - Impact: Security Critical — audit log review, session management
 * - Stakeholders: Tenant Admins, Security Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer (React 18)
 * - Pattern: Dashboard with 3 sections, auto-refresh
 * - Dependencies: apiClient, Sprint 79L security.py backend
 *
 * @cross-references
 * - Backend: backend/app/api/v2/tenant_admin/security.py
 * - Sprint Plan: SPRINT-079L-TA-SECURITY-AUDIT-BOT-CONFIG-LOCAL.md
 */

import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClientV2 as apiClient } from "@api/v2";

interface SecurityOverview {
  active_sessions: number;
  failed_logins_24h: number;
  password_resets_7d: number;
  locked_accounts: number;
  security_score: number;
}

interface AuditEntry {
  id: string;
  action: string;
  action_label_vi: string;
  ip_address?: string;
  success: boolean;
  created_at?: string;
}

interface ActiveSession {
  user_id: string;
  user_name: string;
  ip_address?: string;
  last_activity?: string;
  device_info?: string;
}

const TenantSecurityDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<SecurityOverview | null>(null);
  const [events, setEvents] = useState<AuditEntry[]>([]);
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [ovRes, evRes, sesRes] = await Promise.allSettled([
        apiClient.get("/api/v2/tenant-admin/security/overview"),
        apiClient.get("/api/v2/tenant-admin/security/audit-logs?limit=20"),
        apiClient.get("/api/v2/tenant-admin/security/active-sessions"),
      ]);
      if (ovRes.status === "fulfilled") setOverview(ovRes.value.data);
      if (evRes.status === "fulfilled") setEvents(evRes.value.data.logs || []);
      if (sesRes.status === "fulfilled") setSessions(sesRes.value.data.sessions || []);
    } catch (err) {
      console.error("Security data fetch error:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleTerminateSession = async (userId: string) => {
    if (!confirm("Bạn có chắc muốn chấm dứt tất cả phiên của người dùng này?")) return;
    try {
      await apiClient.delete(`/api/v2/tenant-admin/security/sessions/${userId}/terminate`);
      fetchData();
    } catch (err) {
      alert("Lỗi khi chấm dứt phiên");
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "#22c55e";
    if (score >= 60) return "#f59e0b";
    return "#dc2626";
  };

  return (
    // 2026-08-25 (UI-UX-DECISION-REGISTER.md luật 9, rollout): bỏ maxWidth
    // 1200px + margin:auto — kéo sát lề khả dụng ở Desktop.
    <div style={{ padding: "24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h1 style={{ fontSize: "24px", fontWeight: "bold", color: "#1f2937", margin: 0 }}>
          Bảo mật & Kiểm toán
        </h1>
        <button
          onClick={() => navigate("/tenant-admin/security/audit-logs")}
          style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "14px" }}
        >
          Xem nhật ký đầy đủ →
        </button>
      </div>

      {isLoading && <div style={{ textAlign: "center", padding: "24px", color: "#6b7280" }}>Đang tải...</div>}

      {/* Section 1: Security Overview KPIs */}
      {overview && (
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
          <div style={{ flex: 1, minWidth: "180px", background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", textAlign: "center" }}>
            <div style={{ fontSize: "36px", fontWeight: "bold", color: getScoreColor(overview.security_score) }}>
              {overview.security_score}
            </div>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>Điểm bảo mật</div>
          </div>
          <div style={{ flex: 1, minWidth: "180px", background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", borderLeft: "4px solid #2563eb" }}>
            <div style={{ fontSize: "28px", fontWeight: "bold", color: "#1f2937" }}>{overview.active_sessions}</div>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>Phiên đang hoạt động</div>
          </div>
          <div style={{ flex: 1, minWidth: "180px", background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", borderLeft: "4px solid #dc2626" }}>
            <div style={{ fontSize: "28px", fontWeight: "bold", color: overview.failed_logins_24h > 5 ? "#dc2626" : "#1f2937" }}>
              {overview.failed_logins_24h}
            </div>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>Đăng nhập thất bại (24h)</div>
          </div>
          <div style={{ flex: 1, minWidth: "180px", background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", borderLeft: "4px solid #f59e0b" }}>
            <div style={{ fontSize: "28px", fontWeight: "bold", color: "#1f2937" }}>{overview.password_resets_7d}</div>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>Đặt lại mật khẩu (7 ngày)</div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        {/* Section 2: Recent Security Events */}
        <div style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px", color: "#1f2937" }}>
            Sự kiện gần đây
          </h3>
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {events.length === 0 ? (
              <div style={{ color: "#9ca3af", textAlign: "center", padding: "24px" }}>Không có sự kiện</div>
            ) : (
              events.slice(0, 15).map((ev) => (
                <div key={ev.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <div>
                    <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", background: ev.success ? "#22c55e" : "#dc2626", marginRight: "8px" }} />
                    <span style={{ fontSize: "13px", color: "#374151" }}>{ev.action_label_vi || ev.action}</span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                    {ev.ip_address || ""} {ev.created_at ? new Date(ev.created_at).toLocaleTimeString("vi-VN") : ""}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Section 3: Active Sessions */}
        <div style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "600", marginBottom: "16px", color: "#1f2937" }}>
            Phiên đang hoạt động ({sessions.length})
          </h3>
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {sessions.length === 0 ? (
              <div style={{ color: "#9ca3af", textAlign: "center", padding: "24px" }}>Không có phiên nào</div>
            ) : (
              sessions.map((s, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f3f4f6" }}>
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: "500", color: "#1f2937" }}>{s.user_name}</div>
                    <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                      {s.ip_address || "IP không xác định"} • {s.device_info ? s.device_info.substring(0, 40) + "..." : ""}
                    </div>
                  </div>
                  <button
                    onClick={() => handleTerminateSession(s.user_id)}
                    style={{ padding: "4px 10px", background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: "4px", cursor: "pointer", fontSize: "12px" }}
                  >
                    Chấm dứt
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantSecurityDashboard;
